import Link from 'next/link';
import Trans from 'next-translate/Trans';
import useLocale from 'next-translate/useTranslation';
import { FaWhatsapp } from 'react-icons/fa6';

import styles from './agents.module.css';

import clsxm from '@/lib/clsxm';

import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import { AgentBubble, AgentReply, VisitorBubble } from './ChatBubbles';
import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  CheckGlyph,
  delay,
  Stage,
  tone,
  useOpenAgentDemo,
} from './primitives';

/** The demo business in the hero. Its colour is deliberately not ours. */
const HOTEL = 'Hotel Marisol';
const HOTEL_COLOR = 'bg-[#b64d27]';

export default function AgentsHero() {
  const { t } = useLocale('chatbots');
  const openDemo = useOpenAgentDemo();

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto max-w-7xl px-6 pt-12 sm:pt-20 lg:px-8'>
        <a
          href='#voice'
          className={clsxm(
            styles.enter,
            'group inline-flex max-w-full items-center gap-2.5 rounded-full bg-white py-1 pe-3.5 ps-1 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] transition hover:ring-[#0f0f28]/20 dark:bg-white/[0.05] dark:ring-white/10 dark:hover:ring-white/25',
            tone.body,
          )}
        >
          <span className='flex-none rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white'>
            {t('New')}
          </span>
          <span className='truncate'>
            {t('Your agent now answers phone calls')}
          </span>
          <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5' />
        </a>

        <div className='mt-8 grid gap-x-14 gap-y-8 lg:grid-cols-12 lg:items-end'>
          <h1
            className={clsxm(
              styles.display,
              styles.enter,
              'text-[2.875rem] font-semibold leading-[1.02] sm:text-7xl sm:leading-[1] lg:col-span-7 lg:text-[5.25rem]',
              tone.ink,
            )}
            style={delay(60)}
          >
            <Trans
              i18nKey='hero-headline'
              ns='chatbots'
              components={[
                <span
                  key='0'
                  className='text-primary-600 dark:text-primary-400'
                />,
              ]}
            />
          </h1>

          <div
            className={clsxm(styles.enter, 'lg:col-span-5 lg:pb-2')}
            style={delay(160)}
          >
            <p
              className={clsxm(
                'text-lg leading-8 sm:text-[1.3125rem] sm:leading-[1.6]',
                tone.body,
              )}
            >
              {t(
                'Train an agent on your website and documents. It answers customers on your site, WhatsApp, email and the phone, books appointments and collects leads, day and night.',
              )}
            </p>
            <div className='mt-8 flex flex-wrap items-center gap-3'>
              <Link href='/login?preset=bots' className={buttonPrimary}>
                {t('Build your agent free')}
                <ArrowGlyph />
              </Link>
              <button
                type='button'
                onClick={openDemo}
                className={buttonSecondary}
              >
                <span className='relative flex h-2 w-2'>
                  <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none' />
                  <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-500' />
                </span>
                {t('Talk to our agent')}
              </button>
            </div>
            <p className={clsxm('mt-5 text-sm', tone.quiet)}>
              {t('No credit card required')} ·{' '}
              {t('Ready in minutes, no code needed')}
            </p>
          </div>
        </div>
      </div>

      <div className='mx-auto mt-14 max-w-7xl px-3 sm:mt-20 sm:px-6 lg:px-8'>
        <HeroStage />
      </div>
    </header>
  );
}

function HeroStage() {
  const { t } = useLocale('chatbots');

  return (
    <Stage
      variant='indigo'
      halftoneAt='100% 0%'
      className={clsxm(
        styles.enter,
        'px-4 py-8 sm:px-10 sm:py-12 lg:h-[40.5rem] lg:p-0',
      )}
      style={delay(240)}
    >
      <p className='sr-only'>
        {t(
          'Example: one AI agent answering a hotel’s website chat, a phone call and a WhatsApp message at the same time.',
        )}
      </p>
      <div
        aria-hidden='true'
        className='relative mx-auto flex max-w-5xl flex-col items-center gap-5 lg:block lg:h-full'
      >
        <div className='w-full max-w-[23rem] lg:absolute lg:left-6 lg:top-[8.5rem] lg:w-[17.5rem] xl:left-2'>
          <div className={styles.enter} style={delay(900)}>
            <CallCard />
          </div>
        </div>

        <div className='w-full max-w-[23.5rem] lg:absolute lg:left-1/2 lg:top-12 lg:-translate-x-1/2'>
          <div className={styles.enter} style={delay(380)}>
            <HotelWidget />
          </div>
        </div>

        <div className='hidden w-full max-w-[23rem] sm:block lg:absolute lg:right-6 lg:top-[18rem] lg:w-[18.5rem] xl:right-2'>
          <div className={styles.enter} style={delay(1500)}>
            <WhatsAppCard />
          </div>
        </div>
      </div>
    </Stage>
  );
}

function HotelWidget() {
  const { t } = useLocale('chatbots');

  return (
    <div className='overflow-hidden rounded-[22px] bg-white text-start shadow-[0_40px_90px_-30px_rgba(22,12,90,0.75)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10'>
      <div className='flex items-center gap-3 border-b border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
        <span
          className={clsxm(
            'flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-semibold text-white',
            HOTEL_COLOR,
          )}
        >
          M
        </span>
        <div className='min-w-0 flex-1'>
          <p className={clsxm('truncate text-[15px] font-semibold', tone.ink)}>
            {HOTEL}
          </p>
          <p className={clsxm('flex items-center gap-1.5 text-xs', tone.quiet)}>
            <span className='h-1.5 w-1.5 rounded-full bg-emerald-500' />
            {t('Replies instantly')}
          </p>
        </div>
        <span className='flex h-8 w-8 items-center justify-center rounded-full bg-[#b64d27]/10 text-[#b64d27] dark:bg-[#b64d27]/20 dark:text-[#f0a383]'>
          <PhoneGlyph className='h-4 w-4' />
        </span>
      </div>

      <div className='space-y-3 px-5 py-5 text-[14px] leading-relaxed'>
        <VisitorBubble at={600}>
          {t('Hi! Do you have a double room this Friday?')}
        </VisitorBubble>
        <AgentReply typingAt={1100} at={1900}>
          <AgentBubble brand={HOTEL_COLOR}>
            {t(
              'We do. A sea-view double is €190 a night with breakfast. Shall I book it for you?',
            )}
          </AgentBubble>
        </AgentReply>
        <VisitorBubble at={2900}>{t('Yes please, two nights.')}</VisitorBubble>
        <AgentReply typingAt={3300} at={4100}>
          <div className='flex max-w-[85%] items-center gap-3 rounded-2xl bg-[#f6f6f9] px-3.5 py-2.5 ring-1 ring-black/[0.05] dark:bg-white/[0.05] dark:ring-white/[0.06]'>
            <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
              <CheckGlyph className='h-3.5 w-3.5' />
            </span>
            <span className='min-w-0'>
              <span
                className={clsxm(
                  'block text-[13px] font-semibold leading-tight',
                  tone.ink,
                )}
              >
                {t('Booking confirmed')}
              </span>
              <span className={clsxm('block text-xs', tone.quiet)}>
                {t('Fri to Sun · Sea-view double')}
              </span>
            </span>
          </div>
        </AgentReply>
        <AgentReply at={4700}>
          <AgentBubble brand={HOTEL_COLOR}>
            {t('All set! Your confirmation is on its way to your inbox.')}
          </AgentBubble>
        </AgentReply>
      </div>

      <div className='px-4 pb-3'>
        <div className='flex items-center gap-2 rounded-full bg-[#f3f3f7] py-2.5 pe-2.5 ps-4 dark:bg-white/[0.05]'>
          <span className={clsxm('flex-1 truncate text-sm', tone.quiet)}>
            {t('Ask anything…')}
          </span>
          <span
            className={clsxm(
              'flex h-7 w-7 items-center justify-center rounded-full text-white',
              HOTEL_COLOR,
            )}
          >
            <ArrowGlyph className='h-3.5 w-3.5 -rotate-90 rtl:rotate-90' />
          </span>
        </div>
        <p className={clsxm('mt-2.5 text-center text-[11px]', tone.quiet)}>
          Powered by Easy-Peasy.AI
        </p>
      </div>
    </div>
  );
}

function CallCard() {
  const { t } = useLocale('chatbots');
  const bars = [
    6, 14, 22, 12, 26, 18, 30, 22, 10, 24, 32, 20, 14, 28, 16, 8, 20, 26, 12, 6,
  ];

  return (
    <div className='rounded-2xl bg-[#0d0c1f] p-4 text-start text-white shadow-[0_30px_70px_-25px_rgba(8,6,40,0.8)] ring-1 ring-white/10'>
      <div className='flex items-center justify-between'>
        <span className='flex items-center gap-2 text-xs font-medium text-white/60'>
          <PhoneGlyph className='h-3.5 w-3.5' />
          {t('Live call')}
        </span>
        <span className='flex items-center gap-1.5 text-xs font-medium tabular-nums text-emerald-400'>
          <span className='h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400 motion-reduce:animate-none' />
          00:42
        </span>
      </div>
      <p className='mt-2 text-sm font-semibold tabular-nums' dir='ltr'>
        +1 (415) 555-0132
      </p>
      <div className={clsxm(styles.wave, 'mt-3 flex h-8 items-end gap-[3px]')}>
        {bars.map((height, index) => (
          <span
            key={index}
            className='w-[4px] rounded-full bg-primary-400'
            style={{ height: `${height}px`, ...delay(index * 70) }}
          />
        ))}
      </div>
      <div className='mt-3 space-y-2 text-[13px] leading-snug'>
        <p className='flex gap-2 text-white/55'>
          <span className='mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-white/40' />
          {t('Is parking included?')}
        </p>
        <p className='flex gap-2 text-white'>
          <span
            className={clsxm(
              'mt-[7px] h-1.5 w-1.5 flex-none rounded-full',
              HOTEL_COLOR,
            )}
          />
          {t('Yes, parking is free for our guests.')}
        </p>
      </div>
    </div>
  );
}

function WhatsAppCard() {
  const { t } = useLocale('chatbots');

  return (
    <div className='overflow-hidden rounded-2xl bg-[#efeae2] text-start shadow-[0_30px_70px_-25px_rgba(8,6,40,0.7)] ring-1 ring-black/5 dark:bg-[#0b141a] dark:ring-white/10'>
      <div className='flex items-center gap-2.5 bg-[#075e54] px-4 py-3 text-white dark:bg-[#1f2c34]'>
        <FaWhatsapp className='h-4 w-4 flex-none' />
        <span className='truncate text-sm font-semibold'>{HOTEL}</span>
      </div>
      {/* Deliberately not translated: the point is that the agent switches. */}
      <div
        className='space-y-2 px-3 py-3 text-[13px] leading-snug'
        lang='es'
        dir='ltr'
      >
        <p className='me-10 rounded-lg rounded-ss-none bg-white px-3 py-2 text-[#111b21] shadow-sm dark:bg-[#202c33] dark:text-[#e9edef]'>
          ¿Puedo hacer el check-in a las 11?
          <span className='ms-2 text-[10px] text-[#667781] dark:text-[#8696a0]'>
            10:24
          </span>
        </p>
        <p className='ms-10 rounded-lg rounded-se-none bg-[#d9fdd3] px-3 py-2 text-[#111b21] shadow-sm dark:bg-[#005c4b] dark:text-[#e9edef]'>
          ¡Claro! Te esperamos a las 11:00.
          <span className='ms-2 text-[10px] text-[#667781] dark:text-[#8fc4b8]'>
            10:24 ✓✓
          </span>
        </p>
      </div>
      <p className='mx-3 mb-3 flex items-center justify-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-center text-[11px] font-medium text-[#3b4a54] dark:bg-white/10 dark:text-[#c5d0d6]'>
        <GlobeGlyph className='h-3.5 w-3.5 flex-none' />
        {t('Replied in Spanish')}
      </p>
    </div>
  );
}

function GlobeGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      aria-hidden='true'
      className={className}
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M12 21a9 9 0 100-18 9 9 0 000 18zm0 0c2.5-2.4 3.75-5.4 3.75-9S14.5 5.4 12 3m0 18c-2.5-2.4-3.75-5.4-3.75-9S9.5 5.4 12 3M3.6 9h16.8M3.6 15h16.8'
      />
    </svg>
  );
}

export function PhoneGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      aria-hidden='true'
      className={className}
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z'
      />
    </svg>
  );
}
